/**
 * The lobby connection (backend.md §3.8).
 *
 * `LobbyRoom` pushes the whole public room list continuously, so the list is
 * live without polling and private rooms are structurally absent — they are
 * never put on the channel in the first place, rather than filtered here.
 *
 * Creating a room is the one flow that has to hand a live connection across a
 * navigation. `client.create()` both creates *and* joins; navigating to
 * `/room/:id` and joining again would open a second connection and, because the
 * lobby phase frees a seat on leave, could cost the creator their seat. So the
 * created room is parked in a module handoff that `useJoinRoom` adopts. A reload
 * finds the handoff empty and falls back to the reconnection token.
 */

import type { Room } from 'colyseus.js';
import { useEffect } from 'react';

import type { LobbyChatMessage, RoomConfig, RoomSummary, TestRoomOptions } from '@ordago/shared';

import { useStore } from '../store';

import { colyseus, LOBBY_ROOM_NAME, MUS_ROOM_NAME } from './colyseus';
import { storeReconnectionToken } from './useJoinRoom';

// ───────────────────────────────────────────────────────────── created-room handoff

let handoff: Room<unknown> | null = null;

export function takeCreatedRoom(roomId: string): Room<unknown> | null {
  if (handoff === null || handoff.roomId !== roomId) return null;
  const room = handoff;
  handoff = null;
  return room;
}

/**
 * `test` is ignored unless the server booted with `TEST_MODE=1`, which
 * `parseEnv` refuses in production — it is what makes a scripted four-client
 * game deal the same cards every run (frontend.md §8.2).
 */
export async function createRoom(config: RoomConfig, test?: TestRoomOptions): Promise<string> {
  const token = useStore.getState().auth.accessToken;
  const room = await colyseus().create(MUS_ROOM_NAME, {
    ...config,
    token,
    ...(test === undefined ? {} : { test }),
  });
  storeReconnectionToken(room.roomId, room.reconnectionToken);
  handoff = room;
  return room.roomId;
}

// ──────────────────────────────────────────────────────────────── lobby projection

type Raw = Record<string, unknown>;

function str(o: Raw, k: string, d = ''): string {
  const v = o[k];
  return typeof v === 'string' ? v : d;
}
function num(o: Raw, k: string, d = 0): number {
  const v = o[k];
  return typeof v === 'number' && Number.isFinite(v) ? v : d;
}
function bool(o: Raw, k: string, d = false): boolean {
  const v = o[k];
  return typeof v === 'boolean' ? v : d;
}

function iterate(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value !== null && typeof value === 'object' && Symbol.iterator in value) {
    return Array.from(value as Iterable<unknown>);
  }
  return [];
}

/** Same reflection-decoding caveat as `net/schema.ts`: total, never throwing. */
function projectRooms(state: unknown): RoomSummary[] {
  const raw = (state ?? {}) as Raw;
  const source = 'rooms' in raw ? raw['rooms'] : state;
  return iterate(source).map((entry): RoomSummary => {
    const r = (entry ?? {}) as Raw;
    const variant = str(r, 'variant', '8r');
    const pace = str(r, 'paceTier', 'normal');
    const senas = str(r, 'senasMode', 'private');
    const phase = str(r, 'phase', str(r, 'status', 'lobby'));
    return {
      roomId: str(r, 'roomId'),
      name: str(r, 'name'),
      variant: variant === '4r' ? '4r' : '8r',
      targetPoints: num(r, 'targetPoints', 40) === 30 ? 30 : 40,
      paceTier:
        (['sinReloj', 'tranquilo', 'normal', 'rapido', 'muyRapido'] as const).find(
          (t) => t === pace,
        ) ?? 'normal',
      senasMode: senas === 'off' || senas === 'catchable' ? senas : 'private',
      vaca: bool(r, 'vaca'),
      occupancy: num(r, 'occupancy'),
      maxClients: num(r, 'maxClients', 4),
      hasBots: bool(r, 'hasBots'),
      ranked: bool(r, 'ranked', true),
      phase: phase === 'playing' || phase === 'finished' ? phase : 'lobby',
    };
  });
}

let lobbyRoom: Room<unknown> | null = null;

export function useLobbyRoom(): void {
  const token = useStore((s) => s.auth.accessToken);

  useEffect(() => {
    if (token === null) return;

    let cancelled = false;
    let room: Room<unknown> | undefined;
    const lobby = useStore.getState().lobby;
    lobby.setConnection('connecting');

    void (async () => {
      try {
        const joined = await colyseus().joinOrCreate(LOBBY_ROOM_NAME, { token });
        if (cancelled) {
          void joined.leave();
          return;
        }
        room = joined;
        lobbyRoom = joined;

        const apply = (state: unknown): void => {
          const s = useStore.getState().lobby;
          s.setRooms(projectRooms(state));
          s.setOnlineCount(num((state ?? {}) as Raw, 'onlineCount'));
        };
        joined.onStateChange(apply);
        if (joined.state !== undefined && joined.state !== null) apply(joined.state);

        // The lobby has its own two-message protocol, deliberately separate from
        // the table's map so a client cannot send one to the wrong room.
        joined.onMessage<LobbyChatMessage>('lobby.chat', (msg) => {
          useStore.getState().lobby.pushChat(msg);
        });
        joined.onLeave(() => {
          useStore.getState().lobby.setConnection('closed');
        });

        useStore.getState().lobby.setConnection('open');
      } catch {
        // The lobby is a convenience, not a gate: a failed join must still leave
        // the create-room button and the join-by-code field usable.
        if (!cancelled) useStore.getState().lobby.setConnection('closed');
      }
    })();

    return () => {
      cancelled = true;
      lobbyRoom = null;
      void room?.leave();
      useStore.getState().lobby.reset();
    };
  }, [token]);
}

/** Sends into the lobby chat; a no-op when the lobby room never connected. */
export function sendLobbyChat(text: string): void {
  lobbyRoom?.send('lobby.chat', { text });
}

/**
 * The live lobby state, for the debug hook only — everything in the app reads
 * the store. It exists for the same reason `heldRoom` does: comparing the mirror
 * against the schema it was projected from is how a field-name or encoding
 * mismatch gets localised instead of guessed at.
 */
export function heldLobbyRoom(): Room<unknown> | null {
  return lobbyRoom;
}
